<template>
  <div class="space-y-1.5">
    <!-- Inline viz (sparkline / KPI strip) when the result is chartable. -->
    <InlineViz v-if="!block.is_error" :output="block.output" />
    <details class="overflow-hidden rounded-lg border border-line bg-surface-2">
    <summary class="flex cursor-pointer select-none items-center gap-2 px-3 py-2 text-xs">
      <span
        class="flex size-5 shrink-0 items-center justify-center rounded-md"
        :class="block.is_error ? 'bg-danger-soft text-danger' : 'bg-success-soft text-success'"
      >
        <component :is="block.is_error ? X : Check" class="size-3" />
      </span>
      <span class="font-medium text-ink">{{ block.is_error ? 'Tool error' : 'Tool result' }}</span>
      <Badge :tone="block.is_error ? 'danger' : 'success'" class="ml-auto">
        {{ block.is_error ? 'Error' : 'OK' }}
      </Badge>
      <ChevronDown class="size-3.5 text-faint transition-transform [details[open]_&]:rotate-180" />
    </summary>
    <div class="border-t border-line p-2">
      <div class="relative">
        <pre class="max-h-48 overflow-auto rounded-md bg-code p-2.5 pr-14 font-mono text-[11px] leading-relaxed text-code-ink">{{ pretty }}</pre>
        <button
          type="button"
          class="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-1.5 py-1 text-[11px] text-gray-300 hover:bg-white/10"
          :title="copied ? 'Copied' : 'Copy'"
          @click="copy"
        >
          <component :is="copied ? Check : Copy" class="size-3" />
        </button>
      </div>
    </div>
    </details>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Check, X, ChevronDown, Copy } from 'lucide-vue-next';
import Badge from '../Badge.vue';
import InlineViz from './InlineViz.vue';
import type { TraceBlock } from '../../stores/chat';

const props = defineProps<{ block: TraceBlock }>();
const pretty = computed(() => {
  const out = props.block.output;
  if (typeof out === 'string') {
    // Try to parse JSON for pretty display.
    try {
      return JSON.stringify(JSON.parse(out), null, 2);
    } catch {
      return out;
    }
  }
  return JSON.stringify(out ?? null, null, 2);
});

const copied = ref(false);
async function copy(): Promise<void> {
  try {
    await navigator.clipboard.writeText(pretty.value);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  } catch { /* clipboard blocked (insecure context) — ignore */ }
}
</script>
